import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { whiteNotchedOutline } from '../../../../../../../shared/whiteNotchedOutline';

const PanelHeader = styled('header')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '8px 16px'
});

const PanelBody = styled('div')({
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderTop: 'none',
    padding: '16px',
    minHeight: '56px',
    flexGrow: 1,
    whiteSpace: 'pre-wrap'
});

const EditForm = styled('form')({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
});

const EditActions = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
});

type CommentPanelProps = {
    title: string;
    text: string | null;
    canEdit: boolean;
    isSaving: boolean;
    onSave: (text: string) => Promise<unknown>;
};

export function CommentPanel({ title, text, canEdit, isSaving, onSave }: CommentPanelProps) {
    let [draft, setDraft] = React.useState<string | null>(null);
    let isEditing = draft !== null;

    function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        onSave(draft.trim()).then(() => setDraft(null));
    }

    return (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column' }}>
            <PanelHeader>
                <Typography variant="h3" sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {title}
                </Typography>
                {canEdit && !isEditing && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => setDraft(text || '')}
                    >
                        {Translate('image-processing/page/comments/header/edit-comment')}
                    </Button>
                )}
            </PanelHeader>
            <PanelBody>
                {isEditing ? (
                    <EditForm onSubmit={handleSubmit}>
                        <TextField
                            multiline
                            minRows={3}
                            maxRows={12}
                            fullWidth
                            size="small"
                            value={draft}
                            sx={whiteNotchedOutline}
                            inputProps={{ 'aria-label': title }}
                            onChange={(event) => setDraft(event.target.value)}
                        />
                        <EditActions>
                            <Button
                                variant="outlined"
                                disabled={isSaving}
                                onClick={() => setDraft(null)}
                            >
                                {Translate('image-processing/page/comment/button-label/cancel')}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSaving}
                                aria-busy={isSaving}
                                startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
                            >
                                {Translate('image-processing/page/comment/button-label/save')}
                            </Button>
                        </EditActions>
                    </EditForm>
                ) : (
                    <Typography variant="body1" sx={{ fontSize: '14px' }}>
                        {text}
                    </Typography>
                )}
            </PanelBody>
        </Box>
    );
}

type CommentPanelSkeletonProps = {
    canEdit: boolean;
};

export function CommentPanelSkeleton({ canEdit }: CommentPanelSkeletonProps) {
    return (
        <Box component="section" aria-busy="true" data-testid="comment-panel-skeleton">
            <PanelHeader>
                <Skeleton variant="text" animation="wave" width={180} sx={{ fontSize: '14px' }} />
                {canEdit && <Skeleton variant="rounded" animation="wave" width={120} height={31} />}
            </PanelHeader>
            <PanelBody>
                <Skeleton variant="text" animation="wave" width="90%" sx={{ fontSize: '14px' }} />
                <Skeleton variant="text" animation="wave" width="60%" sx={{ fontSize: '14px' }} />
            </PanelBody>
        </Box>
    );
}
